import { useState, useEffect } from "react";
import { TrendingUp, Users, DollarSign, ShoppingCart, BookOpen, BarChart3 } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from "recharts";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

const COLORS = ["#0284C7", "#7C3AED", "#059669", "#D97706", "#DC2626", "#2563EB", "#64748B", "#0891B2"];
const TOOLTIP_STYLE = { background: "#111827", border: "none", color: "#FFF", fontSize: 12 };
const AXIS_TICK_SM = { fontSize: 11, fill: "#94A3B8" };
const AXIS_TICK = { fontSize: 12, fill: "#94A3B8" };

export default function AdminAnalytics() {
  const [overview, setOverview] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [examData, setExamData] = useState([]);
  const [contentStats, setContentStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview"),
      api.get("/analytics/revenue-chart"),
      api.get("/analytics/popular-exams"),
      api.get("/analytics/content-stats"),
    ]).then(([ov, rev, exams, content]) => {
      setOverview(ov.data);
      setRevenueData(rev.data);
      setExamData(exams.data);
      setContentStats(content.data);
    }).catch((err) => console.error(err)).finally(() => setLoading(false));
  }, []);

  if (loading) return <AdminLayout><div className="admin-text-secondary py-12 text-center">Loading analytics...</div></AdminLayout>;

  const kpis = overview ? [
    { label: "Total Revenue", value: `₹${(overview.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: "text-green-500", bg: "bg-green-50" },
    { label: "Total Orders", value: overview.total_orders, icon: ShoppingCart, color: "text-blue-500", bg: "bg-blue-50" },
    { label: "Students", value: overview.total_users, icon: Users, color: "text-purple-500", bg: "bg-purple-50" },
    { label: "Courses", value: overview.total_courses, icon: BookOpen, color: "text-amber-500", bg: "bg-amber-50" },
  ] : [];

  const contentData = contentStats ? [
    { name: "Courses", count: contentStats.courses_by_type?.course || 0 },
    { name: "Books", count: contentStats.courses_by_type?.book || 0 },
    { name: "Videos", count: contentStats.total_videos || 0 },
    { name: "Chapters", count: contentStats.total_chapters || 0 },
  ] : [];

  return (
    <AdminLayout>
      <div data-testid="admin-analytics-page">
        <div className="mb-6">
          <h1 className="text-2xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Analytics & Reports
          </h1>
          <p className="text-sm admin-text-secondary mt-0.5">Detailed insights into your platform performance</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {kpis.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className="admin-card border p-4 flex items-center gap-3">
                <div className={`w-10 h-10 ${kpi.bg} flex items-center justify-center`}>
                  <Icon size={18} className={kpi.color} />
                </div>
                <div>
                  <p className="text-xl font-black admin-text">{kpi.value}</p>
                  <p className="text-[10px] admin-text-secondary font-medium">{kpi.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Revenue Chart - Full Width */}
        <div className="admin-card border p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-[#0284C7]" />
            <h2 className="text-base font-bold admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Monthly Revenue
            </h2>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0284C7" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#0284C7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => [`₹${v}`, "Revenue"]} />
                <Area type="monotone" dataKey="revenue" stroke="#0284C7" strokeWidth={2.5} fill="url(#revenueGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Orders by Month */}
          <div className="admin-card border p-5">
            <h2 className="text-base font-bold admin-text mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Orders Per Month
            </h2>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={AXIS_TICK_SM} />
                  <YAxis tick={AXIS_TICK_SM} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="orders" fill="#7C3AED" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Popular Exams */}
          <div className="admin-card border p-5">
            <h2 className="text-base font-bold admin-text mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Courses by Category
            </h2>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={examData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                    {examData.map((entry, i) => <Cell key={`cat-${entry.name || i}`} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Content Stats */}
        <div className="admin-card border p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={16} className="text-[#0284C7]" />
            <h2 className="text-base font-bold admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Content Overview
            </h2>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={contentData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={AXIS_TICK_SM} />
                <YAxis type="category" dataKey="name" tick={AXIS_TICK} width={80} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" fill="#0284C7" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
